import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { COMPANY, IMAGES } from "@/data/content";
import { useSiteContent } from "@/context/SiteContentContext";
import { lineMaskContainer, lineMaskItem } from "../site/Reveal";

export const KineticHero = () => {
  const { home } = useSiteContent();
  const hero = home.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.08]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-[100svh] items-end overflow-hidden bg-white"
    >
      {/* Parallax hero image — no cropping, letterboxed with white padding */}
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0 bg-white">
        <img
          src={hero.image || IMAGES.hero}
          alt="From vintage aeromodels to modern remotely piloted aircraft — the Sharma legacy"
          className="h-full w-full object-contain object-center"
        />
      </motion.div>

      {/* Very light readability wash — keeps all four aircraft visible */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-r from-white/60 via-white/15 to-transparent" />
      <div className="blueprint-grid absolute inset-0 z-[2] opacity-10" />

      {/* Content */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 mx-auto w-full max-w-[1400px] px-5 pb-20 pt-40 sm:px-8 sm:pb-28"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-6 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-saffron" />
          <span className="font-heading text-xs uppercase tracking-[0.28em] text-saffron">
            {hero.overline}
          </span>
        </motion.div>

        {/* Brand lockup kicker */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="mb-4 font-heading text-sm font-semibold uppercase tracking-[0.2em] text-steel"
        >
          {COMPANY.name}
        </motion.p>

        {/* Masked line-by-line headline */}
        <motion.h1
          variants={lineMaskContainer}
          initial="hidden"
          animate="show"
          className="font-heading text-3xl font-black uppercase leading-[1.02] tracking-tight text-ink sm:text-5xl lg:text-6xl"
        >
          {hero.headlineLines.map((line, i) => (
            <span key={i} className="block overflow-hidden py-[0.04em]">
              <motion.span variants={lineMaskItem} className="block">
                {i === hero.headlineLines.length - 1 ? (
                  <>
                    {line.split(" ").slice(0, -1).join(" ")}{" "}
                    <span className="text-saffron">{line.split(" ").slice(-1)}</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-8 max-w-2xl font-body text-base leading-relaxed text-steel sm:text-lg"
        >
          {hero.intro}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <Link
            to="/about"
            data-testid="hero-our-story-btn"
            className="group flex items-center justify-center gap-2 rounded-full bg-saffron px-8 py-4 font-heading font-semibold text-white transition-all duration-300 hover:bg-saffron-soft"
          >
            Our Story
            <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <Link
            to="/products"
            data-testid="hero-explore-products-btn"
            className="group flex items-center justify-center gap-2 rounded-full border border-saffron/40 px-8 py-4 font-heading font-semibold text-saffron transition-all duration-300 hover:border-saffron hover:bg-saffron hover:text-white"
          >
            Explore Products
            <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 right-8 z-10 hidden items-center gap-2 text-steel lg:flex"
      >
        <span className="font-body text-xs uppercase tracking-[0.2em]">Scroll</span>
        <ArrowDown className="h-4 w-4 animate-bounce text-saffron" />
      </motion.div>
    </section>
  );
};
